import { RumInitConfiguration, datadogRum } from "@datadog/browser-rum-slim";

import { logger } from "c7-logger";

import { jwtRedactor } from "datadog-loader/jwt-redactor";

const timingRefs: Record<string, number> = {};

export type {
  RumEvent,
  RumEventDomainContext,
  RumActionEvent,
} from "@datadog/browser-rum-slim";
export interface DataDogCustomTimingOptions {
  name: string;
  timeInMs?: number;
}
export interface DataDogCustomActionOptions {
  name: string;
  context?: object | undefined;
}
export interface DataDogSetGlobalContext {
  name: string;
  context?: object | string | undefined;
}
export interface DataDogRemoveGlobalContext {
  name: string;
}

export interface DataDogAddFeatureFlag {
  flagName: string;
  treatment: string;
}

export const startDatadog = (
  config: Partial<RumInitConfiguration> & {
    //required fields
    applicationId: string;
    clientToken: string;
    service: string;
    env: string;
    version: string;
  }
) => {
  const {
    applicationId = "",
    clientToken = "",
    service = "",
    env = "",
    version = "",
    allowedTracingUrls = undefined,
    sessionSampleRate = 100,
    sessionReplaySampleRate = 0,
    trackUserInteractions = false,
    trackResources = true,
    trackLongTasks = true,
    defaultPrivacyLevel = "mask",
    site = "datadoghq.com",
    beforeSend = () => true,
  } = config;
  const mandatoryFields = { applicationId, clientToken, service, env, version };
  const mandatoryEntries = Object.entries(mandatoryFields);
  if (mandatoryEntries.some(([, val]) => !val)) {
    const missingFields = mandatoryEntries.reduce<string[]>(
      (acc, [key, val]) => {
        if (!val) {
          acc = [...acc, key];
        }
        return acc;
      },
      []
    );
    logger.error("missing mandatoryFields", missingFields);
  } else {
    let defaultPrivacyLevelFinal = defaultPrivacyLevel;
    if (
      defaultPrivacyLevelFinal !== "mask" &&
      defaultPrivacyLevelFinal !== "mask-user-input"
    ) {
      defaultPrivacyLevelFinal = "mask";
    }
    if (datadogRum.getInitConfiguration() === undefined) {
      datadogRum.init({
        applicationId,
        clientToken,
        site,
        service,
        env,
        version,
        sessionSampleRate,
        sessionReplaySampleRate: ["c7-cat", "c7-prod"].includes(env)
          ? 0
          : sessionReplaySampleRate, //disabling replays in cat and prod
        trackUserInteractions,
        trackResources,
        trackLongTasks,
        defaultPrivacyLevel: defaultPrivacyLevelFinal,
        allowedTracingUrls,
        enableExperimentalFeatures: ["feature_flags"],
        // @ts-ignore
        beforeSend: jwtRedactor(beforeSend),
      });
    }
    datadogRum.startSessionReplayRecording();

    datadogRum.setTrackingConsent("granted");
  }
};

export const sendCustomTimingDifference = ({
  startName,
  endName,
  endTimeInMs,
}: {
  startName: string;
  endName: string;
  endTimeInMs?: number;
}) => {
  if (timingRefs[startName] === undefined) {
    logger.warn(`startName ${startName} not found in timingRefs`);
    return;
  }
  if (!endTimeInMs) {
    endTimeInMs = Date.now();
  }
  timingRefs[endName] = endTimeInMs ?? Date.now();

  const diffTimeInMs = endTimeInMs - timingRefs[startName];
  datadogRum.addTiming(endName, diffTimeInMs);
};

export const sendCustomTiming = ({
  name,
  timeInMs,
}: DataDogCustomTimingOptions) => {
  timingRefs[name] = timeInMs ?? Date.now();
  datadogRum.addTiming(name, timeInMs);
};
export const sendCustomAction = ({
  name,
  context,
}: DataDogCustomActionOptions) => {
  datadogRum.addAction(name, context);
};
export const setGlobalContextProperty = ({
  name,
  context,
}: DataDogSetGlobalContext) => {
  datadogRum.setGlobalContextProperty(name, context);
};

export const removeGlobalContextProperty = ({
  name,
}: DataDogRemoveGlobalContext) => {
  datadogRum.removeGlobalContextProperty(name);
};

export const stopDatadogUserTracking = () => {
  datadogRum.setTrackingConsent("not-granted");
  datadogRum.stopSession();
};

export const endDatadogSession = () => {
  datadogRum.stopSession();
};

export const captureFeatureFlag = ({
  flagName,
  treatment,
}: DataDogAddFeatureFlag): void => {
  datadogRum?.addFeatureFlagEvaluation(flagName, treatment);
};
